import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Dropdown,
  Icon,
  Modal,
  Pagination,
  Table,
  Tooltip,
} from "../../../components";
import { Dashboard } from "../../../layout";
import { currencyFormat } from "../../../utils/Formats";
import { Api } from "../../../api/Assinafy";
import "./styles.scss";

const getStatusLabel = (status) => {
  const numericStatusMap = {
    1: "Pendente",
    2: "Pago",
    3: "Recusado",
    4: "Disputado",
    5: "Reembolsado",
    6: "Cancelado",
  };
  
  const stringStatusMap = {
    "pending": "Pendente",
    "paid": "Pago",
    "refused": "Recusado",
    "disputed": "Disputado",
    "refunded": "Reembolsado",
    "cancelled": "Cancelado",
    "canceled": "Cancelado", // variação de spelling
  };
  
  if (typeof status === 'number') {
    return numericStatusMap[status] || "Desconhecido";
  }
  
  if (typeof status === 'string') {
    const lowerStatus = status.toLowerCase();
    return stringStatusMap[lowerStatus] || status; // Retorna o original se não encontrar
  }
  
  return "Desconhecido";
};

const getBillingTypeLabel = (billingType) => {
  const billingTypeMap = {
    CreditCard: "Cartão de Crédito",
    Boleto: "Boleto",
    Pix: "Pix",
  };

  return billingTypeMap[billingType] || billingType || "-";
};

const BillingHistory = () => {
  const isMountedRef = useRef(true);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ pageCount: 4 });
  const [creditCards, setCreditCards] = useState([]);
  const [creditHistory, setCreditHistory] = useState([]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const formatPaymentMethod = (cardLast4, cardBrand) => {
    if (!cardLast4) return "•••• •••• •••• 1234";
    return `•••• •••• •••• ${cardLast4}`;
  };

  const getDocumentCountForPayment = (paymentId, paymentDate, creditsData = null) => {
    const credits = creditsData || creditHistory;
    if (!credits || credits.length === 0) return 0;
    
    const paymentDateObj = new Date(paymentDate);
    const relatedCredits = credits.filter((credit) => {
      if (credit.reference_type === "Purchase" && credit.reference_id === paymentId) {
        return true;
      }
      const creditDate = new Date(credit.created_at);
      return (
        creditDate.getDate() === paymentDateObj.getDate() &&
        creditDate.getMonth() === paymentDateObj.getMonth() &&
        creditDate.getFullYear() === paymentDateObj.getFullYear() &&
        credit.type === "credit" &&
        credit.source === "extra"
      );
    });

    if (relatedCredits.length > 0) {
      return Math.abs(relatedCredits[0].quantity || 0);
    }

    return 0;
  };

  const extractPaginationFromHeaders = (headers) => {
    const pagination = {};
    const headerKeys = Object.keys(headers);
    const getHeader = (key) => {
      const found = headerKeys.find(k => k.toLowerCase() === key.toLowerCase());
      return found ? headers[found] : null;
    };

    const currentPage = getHeader("x-pagination-current-page");
    const totalCount = getHeader("x-pagination-total-count");
    const pageCount = getHeader("x-pagination-page-count");
    const perPage = getHeader("x-pagination-per-page");

    if (currentPage) pagination.currentPage = parseInt(currentPage, 10);
    if (totalCount) pagination.totalCount = parseInt(totalCount, 10);
    if (pageCount) pagination.pageCount = parseInt(pageCount, 10);
    if (perPage) pagination.perPage = parseInt(perPage, 10);

    return Object.keys(pagination).length > 0 ? pagination : null;
  };

  const fetchBillingHistory = async (page = 1) => {
    try {
      setLoading(true);
      
      const [paymentsResponse, subscriptionResponse, cardsResponse, creditsResponse] = await Promise.all([
        Api("payment/index")({ params: { page } }),
        Api("subscription/view")(),
        Api("creditCard/index")().catch(() => null),
        Api("credit/history")(),
      ]);

      if (!isMountedRef.current) return;

      let subscriptionCard = null;
      if (subscriptionResponse?.status === 200 && subscriptionResponse?.data?.data?.credit_card) {
        subscriptionCard = subscriptionResponse.data.data.credit_card;
      }
      
      if (cardsResponse?.status === 200 && cardsResponse?.data?.data) {
        setCreditCards(cardsResponse.data.data);
      }
      
      if (creditsResponse?.status === 200 && creditsResponse?.data?.data) {
        setCreditHistory(creditsResponse.data.data);
      }

      if (paymentsResponse?.status === 200 && paymentsResponse?.data?.data) {
        const cards = cardsResponse?.status === 200 ? cardsResponse.data.data : [];
        const credits = creditsResponse?.status === 200 ? creditsResponse.data.data : [];
        
        const cardInfo = subscriptionCard || (cards.length > 0 ? cards[0] : null);
        
        const transformWithData = (paymentsData) => {
          if (!paymentsData || !Array.isArray(paymentsData)) return [];
          
          return paymentsData.map((item) => {
            const documentCount = getDocumentCountForPayment(item.id, item.created_at || item.due_date, credits);
            
            const cardLast4 = (subscriptionCard && subscriptionCard.digits) || 
                             cardInfo?.last4 || 
                             cardInfo?.last_4 || 
                             (cardInfo?.number ? cardInfo.number.slice(-4) : null) ||
                             item.card_last4;
            
            if (!cardLast4) {
              console.log("Nenhum last4 encontrado para pagamento:", item.id, {
                subscriptionCard,
                cardInfo,
                itemCardLast4: item.card_last4
              });
            }
            
            return {
              date: formatDate(item.due_date || item.created_at),
              desc: item.description || "Cobrança",
              amount: documentCount,
              payment: formatPaymentMethod(
                cardLast4,
                subscriptionCard?.brand || cardInfo?.brand || cardInfo?.card_brand
              ),
              total: item.amount || 0,
              id: item.id,
              status: item.status,
              statusLabel: getStatusLabel(item.status),
              invoice_url: item.invoice_url,
              invoice_url_nfse: item.invoice_url_nfse || item.nfse_url || item.fiscal_note_url || null,
              reference_id: item.reference_id,
              customer_id: item.customer_id,
              cardBrand: subscriptionCard?.brand || cardInfo?.brand || cardInfo?.card_brand,
              ...item,
            };
          });
        };
        
        const transformedData = transformWithData(paymentsResponse.data.data);
        setData(transformedData);
        
        const paginationFromHeaders = extractPaginationFromHeaders(paymentsResponse.headers || {});
        if (paginationFromHeaders) {
          setPagination(paginationFromHeaders);
        } else if (paymentsResponse.data.pagination) {
          setPagination(paymentsResponse.data.pagination);
        } else if (paymentsResponse.data.meta?.pagination) {
          setPagination(paymentsResponse.data.meta.pagination);
        }
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Erro ao buscar histórico de cobrança:", error);
      if (isMountedRef.current) {
        setData([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    fetchBillingHistory();

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  if (loading) {
    return (
      <Dashboard.Content>
        <Dashboard.Header
          returnButton={true}
          title="Histórico de cobrança"
          className="d-flex reverse-row"
        />
        <div className="text-center p-5">Carregando...</div>
      </Dashboard.Content>
    );
  }

  return (
    <>
      <Dashboard.Content>
        <Dashboard.Header
          returnButton={true}
          title="Histórico de cobrança"
          className="d-flex reverse-row"
        />
        {data.length === 0 ? (
          <div className="text-center p-5">
            Nenhum histórico de cobrança encontrado.
          </div>
        ) : (
          <>
            <Table
              rows={data}
              className="table-billing table-bordered table-hover"
              header={[
                { name: "Data" },
                { name: "Descrição" },
                //{ name: "Tipo de cobrança" },
                { name: "Forma de pagamento" },
                { name: "Total" },
                { name: "Situação" },
                { name: "Ações" },
              ]}
              renderRow={(row, i) => <TableItem key={row.id || i} {...row} />}
            />
            {pagination && <Pagination {...pagination} />}
          </>
        )}
      </Dashboard.Content>
    </>
  );
};

const TableItem = ({ ...props }) => {
  const [modalState, setModalState] = useState(false);
  const modalSwitch = () => setModalState(!modalState);

  return (
    <tr>
      <td className="d-flex flex-column d-lg-none">
        <div>{props.date}</div>
        <div className="fw-500">{props.desc}</div>
        {/* <div className="text-muted">Tipo: {getBillingTypeLabel(props.billing_type)}</div> */}
        <div className="d-flex align-items-center gap-2">
          <Icon id="icon-mastercard" size="30" className="icon-credit" />
          {props.payment}
        </div>
      </td>
      <td className="d-none d-lg-table-cell">{props.date}</td>
      <td className="fw-500 d-none d-lg-table-cell">{props.desc}</td>
      {/* <td className="d-none d-lg-table-cell">{getBillingTypeLabel(props.billing_type)}</td> */}
      <td className="d-none d-lg-table-cell">
        {props.billing_type === "CreditCard" && (
          <div className="d-flex align-items-center gap-2">
            <Icon id="icon-mastercard" size="30" className="icon-credit" />
            {props.payment}
          </div>
        )}
        {props.billing_type !== "CreditCard" && (
          <div className="d-flex align-items-center gap-2">
            {getBillingTypeLabel(props.billing_type)}
          </div>
        )}

      </td>
      <td className="fw-500 text-right">{currencyFormat(props.total)}</td>
      <td className="fw-500">{props.statusLabel || getStatusLabel(props.status)}</td>
      <td className="col-action">
        <div className="d-none d-lg-flex gap-2">
          {props.invoice_url && (
            <TooltipButton tooltipText="Ver Fatura" icon="icon-receipt" as="a" href={props.invoice_url} target="_blank" rel="noopener noreferrer" />
          )}
          {props.invoice_url_nfse && (
            <TooltipButton tooltipText="Ver Nota Fiscal" icon="icon-file" as="a" href={props.invoice_url_nfse} target="_blank" rel="noopener noreferrer" />
          )}
        </div>
        <Dropdown
          className="d-lg-none"
          button={<Icon id="icon-dots" />}
          align="right"
        >
          <Button onClick={modalSwitch}>
            <Icon id="icon-eye" size={20} />
            Visualizar
          </Button>
          {props.invoice_url && (
            <Button as="a" href={props.invoice_url} target="_blank" rel="noopener noreferrer">
              <Icon id="icon-receipt" size={20} />
              Ver Fatura
            </Button>
          )}
          {props.invoice_url_nfse && (
            <Button as="a" href={props.invoice_url_nfse} target="_blank" rel="noopener noreferrer">
              <Icon id="icon-file" size={20} />
              Ver Nota Fiscal
            </Button>
          )}
          <Modal.Alert
            onClose={modalSwitch}
            show={modalState}
            title={`${props.desc} - ${props.date}`}
          >
            <div className="modal-body">
              <div className="modal-body-table">
                <div className="d-flex justify-content-between">
                  <div>Quant. documentos</div>
                  <div className="modal-body-value">{props.amount}</div>
                </div>
                <div className="d-flex justify-content-between">
                  <div>Forma de pagamento</div>
                  <div className="d-flex align-items-center gap-2 modal-body-value">
                    <Icon
                      id="icon-mastercard"
                      size="30"
                      className="icon-credit"
                    />
                    {props.payment}
                  </div>
                </div>
                <div className="d-flex justify-content-between">
                  <div>Total</div>
                  <div className="modal-body-value">
                    {currencyFormat(props.total)}
                  </div>
                </div>
              </div>
              {props.invoice_url && (
                <Button variant="border" as="a" href={props.invoice_url} target="_blank" rel="noopener noreferrer" className="btn-block">
                  Ver Fatura
                </Button>
              )}
              {props.invoice_url_nfse && (
                <Button variant="primary" as="a" href={props.invoice_url_nfse} target="_blank" rel="noopener noreferrer" className="btn-block mt-3">
                  Ver Nota Fiscal
                </Button>
              )}
              {!props.invoice_url && !props.invoice_url_nfse && (
                <div className="text-center text-muted py-3">
                  Nenhum documento disponível para este pagamento.
                </div>
              )}
            </div>
          </Modal.Alert>
        </Dropdown>
      </td>
    </tr>
  );
};

const TooltipButton = ({ icon, tooltipText, ...props }) => {
  const [tooltipShow, setTooltipShow] = useState(false);

  const tooltipMouseEnter = () => {
    setTooltipShow(true);
  };

  const tooltipMouseLeave = () => {
    setTooltipShow(false);
  };

  return (
    <Button
      {...props}
      variant="action"
      onMouseEnter={tooltipMouseEnter}
      onMouseLeave={tooltipMouseLeave}
    >
      <Icon id={icon}></Icon>
      <Tooltip show={tooltipShow} placement="bottom">
        {tooltipText}
      </Tooltip>
    </Button>
  );
};

export default BillingHistory;
